import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAlphabeticalSortedPets, usePetPolicies } from '../../hooks';
import {
  clearPolicyNotifications,
  getMultibrandDefaultPolicy,
  getMultibrandPolicies,
  getPolicyDetail,
  loadFolderStructure,
  loadMultiModalData,
  loadPlanSummaryDetails,
  loadPoliciesOverview,
  loadPolicyDetails,
  loadReimbursementMethod,
  loadWhatWeCoverInfo,
  policyDetailCleaned,
  refreshPolicies,
  setSelectedPetId,
  validateBreedMultibrand,
} from '../../actions';

import {
  ADD_ORCA_PET,
  INBOX_DOCS as INBOX_DOCS_ROUTE,
  INSURANCE,
} from '../navigation/routes.constants';
import {
  PRODUCT_FAMILY,
  INBOX_DOCS,
  BANK_ACCOUNT,
} from '../../constants';
import {
  MARKETING_CHANNEL_ID,
  PPU_MARKETING_CHANNELS,
} from '../../theme/theme.constants';

import loadingSpinnerBlue from '../../assets/lottieFiles/loading-blue.json';
import { ReactComponent as AddIcon } from '../../assets/policy/add-generic.svg';

import { CoverageOverview } from './coverageOverview/CoverageOverview';
import { PetCloudBreadcrumbs } from '../myPets/common/PetCloudBreadcrumbs';
import { NoPolicyPets } from './NoPolicyPets';
import { PolicyModals } from './PolicyModals';
import { CurrentPetPolicy } from './CurrentPetPolicy';
import FigoLottie from '../common/FigoLottie';
import { PolicyHeader } from './PolicyHeader';
import { Powerups } from './powerups/Powerups';
import PolicyNotifications from './PolicyNotifications';
import { FileClaimButton } from '../claims/FileClaimButton';
import { CostcoMemberQuoteModal } from '../common/costcoMemberQuote';
import { CareCredit } from './care-credit';
import { ClaimNotification } from './ClaimNotification';

import { BillingAndPayments } from './billingAndPayments';
import { Reimbursement } from './reimbursement';
import { ChangeCoverageFrame } from './changeCoverage/ChangeCoverageFrame';
import {
  ProvidersSelector,
} from '../common/providersSelector/ProvidersSelector';
import { ContactUsModal } from '../sideMenu/contactUs/ContactUsModal';
import { CONTACT_US_STEPS } from '../sideMenu/contactUs/contactUs.constants';
import { ActionButton } from '../common/petCloud/ActionButton';
import { ViewMoreButton } from './ViewMoreButton';
import { objectToBase64, sleep } from '../../services/utils';
import { getLastFourDigits } from './reimbursement/reimbursement.utils';

import './css/PolicySummary.css';
import { PolicyNotificationCCOrcaCanada } from './PolicyNotificationCCOrcaCanada';

const PolicySummary = ({
  onClosePPIGroupModal = () => { },
  onGetQuoteByBrand = () => { },
  showPPIGroupModal = false,
}) => {
  const store = useSelector(({
    billingPayments,
    claims,
    claimsGeneric,
    inboxAndDocs,
    myVets,
    orcaInsurance,
    personalInformation,
    policies,
    session,
  }) => ({
    billingPayments,
    claims,
    claimsGeneric,
    inboxAndDocs,
    myVets,
    orcaInsurance,
    personalInformation,
    policies,
    session,
  }), shallowEqual);
  const { t } = useTranslation('insurance');
  const i18nCommon = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const { petId, policyId } = useParams();
  const history = useHistory();

  const [selectedPolicy, setSelectedPolicy] = useState({});
  const [multiModalVisible, setMultiModalvisible] = useState(false);
  const [multiModalCategoryId, setMultiModalCategoryId] = useState(0);
  const [showPrimaryVetModal, setShowPrimaryVetModal] = useState(false);
  const [openChangeCoverageFrame, setOpenChangeCoverageFrame] = useState(false);
  const [showProviderSelector, setShowProviderSelector] = useState(false);
  const [
    showCostcoMemberQuoteModal,
    setShowCostcoMemberQuoteModal,
  ] = useState(false);
  const [
    showMultiplePoliciesError,
    setShowMultiplePoliciesError,
  ] = useState(false);
  const [showContactUsModal, setShowContactUsModal] = useState(false);

  const petPolicies = usePetPolicies();
  const { allPets } = petPolicies;

  const {
    billingPayments: {
      billingDetails,
      careCreditMethod,
      paymentMethodData,
      policyDetails,
      reimbursementMethod,
    },
    inboxAndDocs: { folderStructure },
    personalInformation,
    personalInformation: {
      userInfo: {
        Companies: companies,
      },
    },
    policies: {
      changeCoverageValidation,
      defaultPolicy,
      isYoungPetModalVisible,
      loading,
      loadingDefaultPolicy,
      loadingOtherPolicy,
      loadingOverview,
      loadingSeeMoreInfo,
      multibrandPolicies,
      multiModalData,
      otherPolicyDetail,
    },
    claims: { newClaimStarted },
    orcaInsurance: { getQuoteValidation },
  } = store;

  const getAlphabeticalSortedPets = useAlphabeticalSortedPets();

  const handleOpenIframe = () => {
    setOpenChangeCoverageFrame(true);
  };

  const loadingInfo = useMemo(() => loading || loadingOtherPolicy
    || loadingDefaultPolicy, [
    loading,
    loadingOtherPolicy,
    loadingDefaultPolicy,
  ]);

  const isGPI = useMemo(() => selectedPolicy
    ?.ProductFamilyId === PRODUCT_FAMILY.GPI, [selectedPolicy]);

  const currentPet = useMemo(
    () => multibrandPolicies.find((pet) => String(petId) === String(pet.PetId)),
    [petId, multibrandPolicies],
  );

  const init = useCallback(() => {
    const petData = getAlphabeticalSortedPets
      ?.find((item) => item.PetId === +petId);
    const policy = policyId
      ? petData?.policies.find((p) => p.Number === policyId)
      : petData?.policies?.[0];
    if (policy?.Number) {
      dispatch(loadPolicyDetails(policy.Number));
      dispatch(loadReimbursementMethod(policy.Number));
      dispatch(loadPlanSummaryDetails(policy.Number));
      dispatch(policyDetailCleaned());
    } else if (policyId && /^\d+$/g.test(policyId)) {
      const currentPolicy = currentPet?.Policies
        ?.find((current) => String(current.PolicyId) === String(policyId)
          || String(current.PolicyNumber) === String(policyId));

      dispatch(getPolicyDetail({
        marketingChannelId: currentPolicy?.PolicyMarketingChannelId,
        policyId,
      }));
    } else if (petId) {
      dispatch(getMultibrandDefaultPolicy(petId));
    }
    setSelectedPolicy(policy);
  }, [currentPet, getAlphabeticalSortedPets, petId, policyId]);

  const onCloseIframe = async () => {
    await sleep(2000);
    setOpenChangeCoverageFrame(false);
    init();
  };

  useEffect(() => {
    if (loading || !allPets.length) {
      return;
    }

    init();
  }, [
    allPets,
    dispatch,
    init,
    loading,
  ]);

  useEffect(() => {
    dispatch(clearPolicyNotifications());
    dispatch(loadFolderStructure());
  }, []);

  const zipCodeMemo = useMemo(() => personalInformation.zipCode,
    [personalInformation]);

  useEffect(() => {
    // is this what really means?
    const isFigoPolicy = policyDetails && selectedPolicy
      && policyDetails.PolicyNumber === selectedPolicy.Number;

    if (isFigoPolicy) {
      const { EffectiveDate } = policyDetails;
      const effectiveDate = EffectiveDate.split('T')[0];

      dispatch(loadPoliciesOverview());
      dispatch(loadWhatWeCoverInfo({
        effectiveDate,
        policyNumber: selectedPolicy.Number,
        stateAbbr: personalInformation.state,
      }));
    }
  }, [
    dispatch,
    selectedPolicy,
    policyDetails,
    zipCodeMemo,
    personalInformation.state,
  ]);

  useEffect(() => {
    const { userInfo: { CustomerId } } = personalInformation;

    if (Object.entries(multiModalData).length === 0) {
      dispatch(loadMultiModalData({ CustomerId }));
    }
  }, [
    dispatch,
    personalInformation.userInfo.CustomerId,
  ]);

  useEffect(() => {
    if (getAlphabeticalSortedPets
      && getAlphabeticalSortedPets.length
      && !petId) {
      dispatch(setSelectedPetId(getAlphabeticalSortedPets[0].PetId));
    }
  }, [dispatch, getAlphabeticalSortedPets, petId]);

  useEffect(() => {
    if (location?.state?.changeCoverageId) {
      dispatch(refreshPolicies());
    }
  }, []);

  useEffect(() => {
    if (!multibrandPolicies.length) {
      dispatch(getMultibrandPolicies());
    }
  }, [multibrandPolicies.length]);

  const onlyFigo = useMemo(() => {
    if (companies.length === 1) {
      const [company] = companies;
      return company.Id === MARKETING_CHANNEL_ID.figo;
    }
    return false;
  }, [companies]);

  const isCostcoAvailable = (webviewData) => webviewData && webviewData.Id
    === MARKETING_CHANNEL_ID.figo && !!webviewData.GetQuoteCostcoUrl;

  useEffect(() => {
    if (getQuoteValidation.requested) {
      const { hasMultipleOrcaPolicies, webviewsData } = getQuoteValidation;

      // multiple policies
      if (hasMultipleOrcaPolicies) {
        setShowMultiplePoliciesError(true);
      } else if (webviewsData.length === 1) {
        // single policy scenario
        const [webviewData] = getQuoteValidation.webviewsData;

        // previous Costco policy
        if (isCostcoAvailable(webviewData)) {
          setShowCostcoMemberQuoteModal(true);
          return;
        }

        // quote by brand
        onGetQuoteByBrand({
          marketingChannelId: webviewData.Id,
          petId: currentPet.PetId,
          quoteUrl: webviewData.GetQuoteUrl,
        });
      } else {
        setShowProviderSelector(true);
      }
    }
  }, [getQuoteValidation]);

  const handleGetAQuote = useCallback(({ petID }) => {
    dispatch(validateBreedMultibrand({ petId: petID }));
  }, [onGetQuoteByBrand, companies]);

  const handleGetQuoteByBrand = () => (providerId) => {
    setShowProviderSelector(false);

    const webviewData = getQuoteValidation.webviewsData
      .find((webview) => webview.Id === providerId) || {};

    if (isCostcoAvailable(webviewData)) {
      setShowCostcoMemberQuoteModal(true);
      return;
    }

    onGetQuoteByBrand({
      marketingChannelId: providerId,
      petId: currentPet.PetId,
      quoteUrl: webviewData.GetQuoteUrl,
    });
  };

  const closeProviderSelector = () => {
    setShowProviderSelector(false);
  };

  const onCloseCostcoMemberModalPress = () => {
    setShowCostcoMemberQuoteModal(false);
  };

  const onContinueWithCostco = () => {
    const webviewData = getQuoteValidation.webviewsData
      .find((webview) => webview.Id === MARKETING_CHANNEL_ID.figo) || {};

    onGetQuoteByBrand({
      marketingChannelId: MARKETING_CHANNEL_ID.figo,
      petId: currentPet.PetId,
      quoteUrl: webviewData.GetQuoteCostcoUrl,
    });

    setShowCostcoMemberQuoteModal(false);
  };

  const onContinueWithStandardQuote = () => {
    const webviewData = getQuoteValidation.webviewsData
      .find((webview) => webview.Id === MARKETING_CHANNEL_ID.figo) || {};

    onGetQuoteByBrand({
      marketingChannelId: MARKETING_CHANNEL_ID.figo,
      petId: currentPet.PetId,
      quoteUrl: webviewData.GetQuoteUrl,
    });

    setShowCostcoMemberQuoteModal(false);
  };

  const policyData = useMemo(() => {
    const pet = multibrandPolicies.find((item) => item.PetId === +petId) || {};
    let policy = defaultPolicy;

    if (policyId) {
      policy = otherPolicyDetail;
    }

    const selectedPetData = pet;
    if (policy?.MarketChannelId === MARKETING_CHANNEL_ID.figo
      || (!policyId && !defaultPolicy)
      || (policyId && !otherPolicyDetail)) {
      const petData = getAlphabeticalSortedPets
        ?.find((item) => item.PetId === +petId);

      const item = (policyId
        ? petData?.policies.find((p) => p.Number === policyId)
        : petData?.policies?.[0]) || {};

      policy = {
        Benefit: item?.Benefit,
        BillingCycle: billingDetails?.PaymentPlan,
        CancellationDate: item?.CancellationDate,
        CareCredit: careCreditMethod,
        CoverageSummary: null,
        CoverageType: item?.CoverageType,
        Deductible: item?.Deductible,
        Extras: item?.PowerUps?.map((powerup) => ({
          Description: powerup.Description,
          Included: powerup.IsIncluded,
          InsuranceModifierId: powerup.InsuranceModifierId,
          Name: powerup.FullName,
          OptionalBenefits: powerup.OptionalBenefits,
        })),
        Id: item?.Number,
        MarketChannelId: MARKETING_CHANNEL_ID.figo,
        NextBillingDate: item?.NextBillingDate,
        PaymentOption: {
          ExpirationMonth: billingDetails?.CCExpMonth,
          ExpirationYear: billingDetails?.CCExpYear,
          FirstNameOnCard: paymentMethodData?.firstName,
          LastFour: billingDetails?.CCCardNumber
            ?? billingDetails?.ACHAccountNumber,
          LastNameOnCard: paymentMethodData?.lastName,
          // WIP: the correct one payment method id, orca working inprogress
          PaymentMethodId: item?.PaymentPlan,
          // TODO: workaround; /summary no translate PaymentType
          PaymentMethodName: item?.PaymentType === BANK_ACCOUNT
            ? i18nCommon.t('paymentMethodNames.bank')
            : i18nCommon.t('paymentMethodNames.card'),
        },
        PetCoverage: policy?.PetCoverages || [],
        PolicyDisplayNumber: item?.Number,
        PolicyEndDate: item?.ExpirationDate,
        PolicySourceId: 0,
        PolicyStartDate: item?.CoverageStartDate,
        ProductFamilyId: item?.ProductFamilyId,
        Reimbursement: item?.Reimbursement,
        ReimbursementOption: {
          AccountNumberLastFour: reimbursementMethod?.AccountNumber,
          CheckAddress: reimbursementMethod?.Address,
          ReimbursementMethodId: reimbursementMethod?.Type,
          RoutingNumberLastFour: getLastFourDigits(reimbursementMethod
            ?.RoutingNumber),
        },
        SelectedPaymentOptionId: '', // TODO:
        SelectedReimbursementOptionId: '',
        Status: item?.Status,
        StatusDisplay: t(`policyStatus-${item?.Status}`),
        TotalPremium: item.PayAmount,
      };
    }

    const coveragesByPet = policy?.PetCoverages
      ?.find((summary) => summary.Pet.Id === +petId);
    const coverageSummary = coveragesByPet?.CoverageSummary;
    const extras = coveragesByPet?.Extras || policy?.Extras || [];

    const data = {
      loading: loadingInfo,
      selectedPet: selectedPetData,
      selectedPolicy: {
        ...policy,
        ...coverageSummary,
        Benefit: policy?.Benefit || coverageSummary?.BenefitAmount,
        CoveragePeriodId: '', // Empty for Figo policies
        Deductible: policy?.Deductible || coverageSummary?.Deductible,
        Extras: extras,
        PetCoverages: policy?.PetCoverages
          ?.filter((coverage) => coverage.Pet.Id !== +petId),
        PetCoveragesByPet: coveragesByPet,
      },
    };

    const isOrcaPolicy = policy?.MarketChannelId !== MARKETING_CHANNEL_ID.figo;
    if (isOrcaPolicy) {
      const details = policy?.ReimbursementOption?.Details || {};
      let multibrandPetPolicy = {};

      // single policy scenario (no policyId value on the query)
      if (!policyId && petId) {
        multibrandPetPolicy = selectedPetData?.Policies?.length
          ? selectedPetData.Policies[0] : {};
      } else {
        // multiple policy scenario
        // from multibrand pet policies endpoint :skull:
        // for Orca policies, policyId always must have a value
        multibrandPetPolicy = (selectedPetData?.Policies || [])
          .find((petPolicy) => String(petPolicy.PolicyId) === String(policyId));
      }

      return {
        ...data,
        selectedPolicy: {
          ...data.selectedPolicy,
          CoveragePeriodId: multibrandPetPolicy?.CoveragePeriodId || '',
          ReimbursementOption: {
            ...data.selectedPolicy.ReimbursementOption,
            CheckAddress: `${details.Address}, ${details.City}`,
          },
        },
      };
    }

    return data;
  }, [
    billingDetails,
    defaultPolicy,
    getAlphabeticalSortedPets,
    loadingInfo,
    multibrandPolicies,
    otherPolicyDetail,
    paymentMethodData,
    policyDetails,
    reimbursementMethod,
    t,
  ]);

  const isFigoPolicy = useMemo(() => {
    if (!policyData?.selectedPolicy?.MarketChannelId) {
      return false;
    }

    return policyData.selectedPolicy
      .MarketChannelId === MARKETING_CHANNEL_ID.figo;
  }, [policyData]);

  // any kind of ppu variation...
  const isPPUPolicy = useMemo(() => {
    if (!policyData?.selectedPolicy?.MarketChannelId) {
      return false;
    }

    return PPU_MARKETING_CHANNELS
      .includes(policyData.selectedPolicy.MarketChannelId);
  }, [policyData]);

  const isSpotCanPolicy = useMemo(() => {
    if (!policyData?.selectedPolicy?.MarketChannelId) {
      return false;
    }

    return policyData.selectedPolicy.MarketChannelId
      === MARKETING_CHANNEL_ID.spotCan;
  }, [policyData]);

  const isOrcaPolicy = useMemo(() => {
    if (!policyData?.selectedPolicy?.MarketChannelId) {
      return false;
    }

    return policyData.selectedPolicy
      .MarketChannelId !== MARKETING_CHANNEL_ID.figo;
  }, [policyData]);

  const getSelectedPolicyDirectory = useCallback((policyNumber) => {
    const directory = folderStructure
      .find((dir) => String(dir.Id) === INBOX_DOCS.policyDocuments);

    if (directory) {
      // TODO: Remove 'Childrens' validation when v2 is complete
      const selectedDirectory = directory
        .Childrens?.find((dir) => dir.Name === policyNumber)
        || directory.Folders?.find((dir) => dir.Name === policyNumber);

      if (selectedDirectory) {
        return selectedDirectory.Id;
      }
    }

    return null;
  }, [folderStructure]);

  const getPolicyDocumentsFolder = useCallback(() => folderStructure
    .find((dir) => dir.Id === INBOX_DOCS.policyDocuments), [folderStructure]);

  const policyDocumentsURL = useMemo(() => {
    let folderName = policyData?.selectedPolicy?.PolicyFolderDescription;
    let folderId = policyData?.selectedPolicy?.PolicyFolderId;
    if (!folderId) {
      folderName = policyData?.selectedPolicy?.PolicyDisplayNumber;
      folderId = getSelectedPolicyDirectory(folderName);
    }

    if (folderName && folderId) {
      const navData = [
        {
          id: INBOX_DOCS.landing,
          name: t('home'),
        },
        {
          id: INBOX_DOCS.policyDocuments,
          name: t('policyDocuments'),
        },
        {
          id: folderId,
          name: folderName,
        },
      ];

      const breadCrumbData = `data=${objectToBase64(navData)}`;

      return `${INBOX_DOCS_ROUTE}/${folderName}/${folderId}?${breadCrumbData}`;
    }

    const defaultFolder = getPolicyDocumentsFolder();
    if (defaultFolder) {
      return `${INBOX_DOCS_ROUTE}/${defaultFolder.Name}/${defaultFolder.Id}`;
    }

    return INBOX_DOCS_ROUTE;
  }, [policyData, getSelectedPolicyDirectory, getPolicyDocumentsFolder]);

  const claimEnabled = useMemo(() => {
    if (!policyData?.selectedPolicy || !policyData?.selectedPet?.Policies) {
      return true;
    }

    const id = policyData.selectedPolicy.Id;
    const currentPolicy = policyData.selectedPet
      .Policies.find((policy) => policy.PolicyId === id
        || policy.PolicyNumber === id);

    // not policy found, then default value is true
    if (!currentPolicy) {
      return true;
    }

    return Boolean(currentPolicy.ClaimEnabled);
  }, [policyData]);

  function onMultiModalClose() {
    setMultiModalvisible(false);
  }

  function onClickLearnMore(categoryId) {
    setMultiModalvisible(true);

    if (isFigoPolicy && categoryId) {
      setMultiModalCategoryId(categoryId);
    }
  }

  function closePrimaryVetModal() {
    setShowPrimaryVetModal(false);
  }

  const onCloseErrorModal = () => {
    setShowMultiplePoliciesError(false);
  };

  const handleAddPetClick = () => {
    history.push(ADD_ORCA_PET, {
      selectedPolicy: policyData.selectedPolicy,
    });
  };

  function renderAddAPetButton() {
    const { selectedPolicy: policy } = policyData;
    if (isFigoPolicy || policy?.MarketChannelId
      === MARKETING_CHANNEL_ID.ppiG
      || PPU_MARKETING_CHANNELS.includes(policy?.MarketChannelId)) {
      return null;
    }

    return (
      <ActionButton
        className="Policy-summary-add-pet-button"
        isSecondaryButton
        leftIconClassName="Policy-summary-add-pet-icon"
        LeftIconSvg={AddIcon}
        noMargin
        onClick={handleAddPetClick}
        title={isOrcaPolicy ? t('coverageOverviewPPU.addPet') : t('addPet')}
      />
    );
  }

  function renderFileAClaimButton() {
    if (policyData?.selectedPet?.Policies?.length === 0
      || isGPI || !policyData?.selectedPolicy?.Id) {
      return null;
    }

    return (
      <FileClaimButton
        className="Policy-summary-file-claim"
        disabled={!claimEnabled}
      />
    );
  }

  function renderPolicyInfo() {
    const isNotPpiOnePack = policyData?.selectedPolicy?.MarketChannelId
      !== MARKETING_CHANNEL_ID.ppiG;

    return (
      <>
        <CoverageOverview
          loading={loadingInfo}
          loadingOverview={loadingOverview}
          marketingChanneld={policyData.selectedPolicy?.MarketChannelId}
          onOpenIframe={handleOpenIframe}
          policies={store.policies}
          selectedPolicy={policyData?.selectedPolicy}
        />

        {isPPUPolicy || isSpotCanPolicy ? null : (
          <Powerups
            company={policyData?.selectedPolicy?.MarketChannelId}
            items={policyData?.selectedPolicy?.Extras}
            onClickLearnMore={onClickLearnMore}
            productFamilyId={policyData?.selectedPolicy?.ProductFamilyId}
          />
        )}

        {isNotPpiOnePack ? (
          <BillingAndPayments
            company={policyData.selectedPolicy?.MarketChannelId}
            isGPI={isGPI}
            selectedPolicy={policyData?.selectedPolicy}
          />
        ) : null}

        {isFigoPolicy && <CareCredit />}

        {isNotPpiOnePack ? (
          <Reimbursement
            selectedPolicy={policyData?.selectedPolicy}
          />
        ) : null}

      </>
    );
  }

  const toggleContactUsModal = useCallback(() => {
    setShowContactUsModal((prev) => !prev);
  }, []);

  function renderContactUsModal() {
    const { policyProvider } = getQuoteValidation;
    let extraText = null;
    let companyStep = CONTACT_US_STEPS.PPI_ONE_PACK;
    const show = showMultiplePoliciesError
      || showPPIGroupModal || showContactUsModal;
    let close = () => { };

    if (showContactUsModal) {
      companyStep = CONTACT_US_STEPS[policyProvider] || CONTACT_US_STEPS.HOME;
      close = toggleContactUsModal;
    } else if (showMultiplePoliciesError) {
      // More than one active policies error
      extraText = <p>{t('multiplePoliciesError')}</p>;
      close = onCloseErrorModal;
      companyStep = CONTACT_US_STEPS[policyProvider] || CONTACT_US_STEPS.HOME;
    } else if (showPPIGroupModal) {
      // add coverage to PPIG
      extraText = (
        <>
          <p>{t('addCoverageDesc')}</p>
          <p>{t('addCoverageDesc2')}</p>
        </>
      );
      close = onClosePPIGroupModal;
      companyStep = CONTACT_US_STEPS.PPI_ONE_PACK;
    }

    return (
      <ContactUsModal
        companyStep={companyStep}
        custom
        customTitle={t('addCoverage')}
        extraText={extraText}
        hasHeader
        onClose={close}
        show={show}
      />
    );
  }

  if (loadingInfo || !currentPet) {
    return (
      <div className="Policy-summary-shimmer-content">
        <FigoLottie
          animationData={loadingSpinnerBlue}
          height={50}
          width={50}
        />
      </div>
    );
  }

  if (openChangeCoverageFrame) {
    return (
      <ChangeCoverageFrame
        onClose={onCloseIframe}
        petId={petId}
        policy={policyData?.selectedPolicy}
      />
    );
  }

  return (
    <div
      className={'Policy-summary-main-container'
        + ` Colors-${policyData.selectedPolicy?.MarketChannelId}`}
    >
      <PetCloudBreadcrumbs
        activeRoute={currentPet.PetName}
        rootRoute={t('title')}
        rootRouteLink={INSURANCE}
      />

      <div className="Policy-summary-title-page-container">
        <h1>{t('title')}</h1>

        <div className="Policy-summary-header-actions">
          {renderAddAPetButton()}

          {renderFileAClaimButton()}
        </div>
      </div>

      <PolicyNotifications petId={petId} policy={selectedPolicy} />

      <PolicyNotificationCCOrcaCanada
        petId={petId}
        policy={policyData?.selectedPolicy}
      />

      {!claimEnabled && (
        <ClaimNotification onContactUsClick={toggleContactUsModal} />
      )}

      {!claimEnabled && renderContactUsModal()}

      <PolicyModals
        changeCoverageValidation={changeCoverageValidation}
        company={policyData?.selectedPolicy?.MarketChannelId}
        loadingSeeMoreInfo={loadingSeeMoreInfo}
        multiModalCategoryId={multiModalCategoryId}
        multiModalData={multiModalData}
        multiModalVisible={multiModalVisible}
        myVetsInfo={store.myVets}
        newClaimStarted={newClaimStarted}
        onClosePrimaryVetModal={closePrimaryVetModal}
        onMultiModalClose={onMultiModalClose}
        petID={petId}
        policyNumber={policyData.selectedPolicy.PolicyDisplayNumber}
        selectedPolicy={selectedPolicy}
        showPrimaryVetModal={showPrimaryVetModal}
      />

      {!!policyData?.selectedPolicy?.Id && !policyData.loading ? (
        <>
          <PolicyHeader company={policyData.selectedPolicy?.MarketChannelId} />

          <div className="Policy-summary-info-container">
            <div className="Policy-summary-title">
              <h2>{t('myPet')}</h2>

              {policyDocumentsURL && (
                <ViewMoreButton
                  isLink
                  text={t('viewPolicyDocuments')}
                  to={policyDocumentsURL}
                />
              )}
            </div>

            <CurrentPetPolicy
              currentPet={policyData.selectedPet}
              loading={policyData.loading}
            />

            {renderPolicyInfo()}
          </div>
        </>
      ) : (
        <>
          <NoPolicyPets
            currentPet={currentPet}
            isYoungPetModalVisible={isYoungPetModalVisible}
            onGetQuote={handleGetAQuote}
            onlyFigo={onlyFigo}
          />

          {!onlyFigo && (
            <ProvidersSelector
              onClose={closeProviderSelector}
              onProviderSelected={handleGetQuoteByBrand(currentPet)}
              show={showProviderSelector}
            />
          )}

          <CostcoMemberQuoteModal
            onClosePress={onCloseCostcoMemberModalPress}
            onContinueWithCostco={onContinueWithCostco}
            onContinueWithStandardQuote={onContinueWithStandardQuote}
            visible={showCostcoMemberQuoteModal}
          />

          {renderContactUsModal()}
        </>
      )}
    </div>
  );
};

export { PolicySummary };
